import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { S10Response } from '../../../interfaces/auth.interface';
import { Company, Period, Scheme, VFlexColumnConfig } from '../../../interfaces/accountEntries.interface';
import { Scenarios } from '../../../constants/scenarios';
import { cleanDuplicates } from '../../../utils/data';
import { mapDataTypeToDevExpressDataType } from '../../../utils/tools';


export interface GridConfig {
    columns: { dataField: string; width: number; visible: boolean, visibleIndex: number, summary?: boolean, fixed: boolean }[];
    sorting: any[];
    filterValue: any;
    selectedRowKeys: any[];
    summaryColumns: string[];
    groupableColumns: string[];
    fixedColumns: string[];
    allowedPageSizes: number[];
    filterPanel: any;
    searchText: string;
    pageIndex: number;
    pageSize: number
}

export interface ActiveGroupField {
    Usuario: string;
    Escenario: string;
    CorrelativoGrupo: number;
    Correlativo: number;
    Orden: number;
    Cabecera: string;
    Campo: string;
    x: number;
    y: number;
}

export interface UserGroups {
    Usuario: string;
    Escenario: string;
    Correlativo: number;
    Descripcion: string;
    Defecto: boolean;
    Publico: boolean;
    Propietario: string;
}

export interface GroupDetailField {
    Usuario: string;
    Escenario: string;
    Correlativo: number;
    Orden: number;
    Cabecera: string;
    Campo: string;
}

export interface ScenarioConfig {
    activeGroup: ActiveGroupField[];
    userGroups: UserGroups[];
    groupDetailsFields: GroupDetailField[];
    vFlexColumnsConfig: VFlexColumnConfig[] | null;
    vFlexColumnsConfigParts: number[];
    gridConfig: GridConfig;
    lastUpdatedGrid: string;
}

export interface ScenarioResponse<T> {
    requestId: string;
    data: T;
    count: number;
    part: number;
    totalItems: number | null;
    scenario: Scenarios;
}

export interface AccountEntries {
    documentResources: DocumentResources[];
    documentData: DocumentData;
    scenarios: Record<Scenarios, ScenarioConfig>;
    companies: Company[];
    scheme: Scheme[];
    periods: Period[];
}
export interface DocumentData {
  NaturalJuridicaSocioNegocio: string
  DNISocioNegocio: string
  RUCSocioNegocio: string
  RUCEmpresa: string
  DesMoneda: string
  Simbolo: string
  SocioNegocio: string
  DesEmpresa: string
  DesSucursal: string
  CodAduana: any
  DesAduana: any
  DesFormaPago: string
  DesProyecto: string
  CodMotivoPago: any
  DesMotivoPago: any
  CodMetodoPago: any
  DesMetodoPago: any
  CodTipoNotaCreditoDebito: any
  DesTipoNotaCreditoDebito: any
  CodTipoRenta: any
  DesTipoRenta: any
  CodCentroDeCosto: any
  DesCentroDeCosto: any
  NroD: string
  CodDocumento: string
  FechaDocumento: string
  FechaVencimiento: string
  CodTipoDocumento: string
  SerieDocumento: string
  NumeroDocumento: string
  CodIdentificador: string
  CodMoneda: string
  CodFormaDePago: string
  TipoCambioEspecial: boolean
  TipoCambio: any
  TipoCambioDia: number
  Descuento: number
  ImpuestoRenta: any
  ImpuestoSolidaridad: any
  TotalAfecto: number
  TotalInafecto: number
  TotalNeto: number
  TotalImpuesto: number
  Total: number
  CodIdentificadorDestino: string
  ValorIGV: number
  NroGuia: any
  Calculado: boolean
  NetoOrigen: number
  IGVOrigen: number
  TotalOrigen: number
  NroDoc: any
  Retenido: boolean
  TipoDocumentoRelacionado: number
  AfectoImpuesto: boolean
  Poliza: any
  FechaVigenciaDel: any
  FechaVigenciaAl: any
  Asignado: number
  CodProyecto: string
  NroCentroDeCosto: any
  Enviado: boolean
  NroVoucher: any
  CreacionUsuario: string
  CreacionFecha: string
  ActualizacionUsuario: string
  ActualizacionFecha: string
  NroMotivoPago: any
  NroMetodoPago: any
  NoEnviar: boolean
  NroRegularizacion: any
  EsRegularizacion: boolean
  NroAutor: any
  Log: any
  Operacion: number
  LogFecha: string
  LogUsuario: string
  NroEnvio: any
  CodProyectoPago: any
  CodPago: any
  Observacion: string
  NroNotaDeVenta: any
  Procedencia: boolean
  Afecto1: number
  Inafecto1: number
  Neto1: number
  Impuesto1: number
  Total1: number
  Afecto2: number
  Inafecto2: number
  Neto2: number
  Impuesto2: number
  Total2: number
  MontoNeto: number
  AfectaRetencion: boolean
  PorcentajeRetencion: number
  MontoRetencion: number
  Estado: number
  DesEstado: string
  Impreso: boolean
  ImpresionUsuario: string
  ImpresionFecha: any
  NumeroComprobanteGalac: any
  NroPeriodoContable: string
  AplicaLibroCompraVenta: boolean
  CodSucursal: string
  FechaRecepcion: any
  NetoOrigen1: number
  IgvOrigen1: number
  TotalOrigen1: number
  NetoOrigen2: number
  IgvOrigen2: number
  TotalOrigen2: number
  NroTalonarioDetalle: any
  MontoDetraccion: number
  MontoDetraccion1: number
  MontoDetraccion2: number
  AplicaGestion: boolean
  BankAccount_ID_Origen: number
  BankAccount_ID_Destino: number
  AsignadoNotaVenta: boolean
  NroGuiaDoc: any
  FechaAplicacionContable: string
  EsServicio: boolean
  Revisado: boolean
  RevisionUsuario: string
  RevisionFecha: string
  TieneNota: boolean
  ValorReferencial: number
  PorcentajePercepcion: number
  MontoPercepcion: number
  NumeroCorrelativo: string
  NroDetraccion: any
  PorcentajeDetraccion: number
  NroPercepcion: any
  RelacionadoCon: any
  DctoRelacionado: any
  OCRelacionado: any
  NumeroConstanciaDetraccion: string
  TotalPagado: number
  TotalSaldo: number
  Ano: string
  AnoMes: string
  NroNVPagoDetalle: any
  GeneraComprobantePercepcion: boolean
  FechaConstanciaDetraccion: any
  MontoLiquidacion: any
  RetencionCobranza: boolean
  NroDocumentoPago: string
  AFPAporte: any
  AFPComision: any
  AFPSeguro: any
  EsperaPercepcion: boolean
  ONPAporte: any
  PercepcionPorDocumento: boolean
  Autodetraccion: boolean
  SinRevisionUsuario: any
  SinRevisionFecha: any
  FactorVenta: number
  TipoBoleto: any
  NroAduana: any
  DocumentoElectronico: boolean
  Pago_ID: any
  C_PaySelectionLine_ID: any
  AfectaPercepcion: boolean
  AfectaDetraccionManual: boolean
  NroTipoNotaCreditoDebito: any
  SerieMaquinaRegistradora: any
  HonorarioLibroCompra: any
  CodProveedorExterior: any
  SerieDocumentoExterior: any
  NumeroDocumentoExterior: any
  FechaDocumentoExterior: any
  NroTipoDeRenta: any
  NroPeriodoContableRectificacion: any
  TotalISC: any
  Aprobado: boolean
  AprobacionUsuario: any
  AprobacionFecha: any
  NroPeriodoContableLibro: string
  CodADAnticipo: any
  NumeroGrupoAnticipo: any
  FechaAbono: any
  TipoProcDocumento: number
  InafectoExonerado1: number
  InafectoExonerado2: number
  Exonerado1: number
  Exonerado2: number
  EnviadoPortalProveedor: any
  FechaEnviadoPortalProveedor: any
  DetraccionPagoNotaCredito: any
  NumeroOC: any
  NoAplicaRetencion: boolean
  TipoBoletajeSperant: number
  NetoGravada1: number
  ImpuestoGravada1: number
  TotalGravada1: number
  NetoNoGravada1: number
  ImpuestoNoGravada1: number
  TotalNoGravada1: number
  NetoAmbosGravada1: number
  ImpuestoAmbosGravada1: number
  TotalAmbosGravada1: number
  OtrosImpuestos1: number
  CalculaParcialUsuario: string
  CalculaParcialFecha: string
  HoraEmision: string
  Provision: boolean
  OtrosConceptos1: number
  OtrosConceptos2: number
  OID: number
  DocumentoNomina: boolean
  DesPeriodoContable: string
  DesEstadoPeriodo: string
  FormaConstanciaDetraccion: string
  CodEstadoFinal: string
  EstadoAprobacion: any
  ObservacionAprobacion: any
  ADRelacionado: any
  EstadoElectronico: number
  EnviadoPortal: boolean
  EmailEnviado: string
  FormaCorrelativo: string
  CodTipoDocumentoOrigen: string
  FormaConstanciaDetraccion1: any
  CorrelativoUsuario: any
  CorrelativoFecha: any
  ClasificacionBienes: any
  DocumentoAsociado: any
  Origen: any
  NetoDeducible1: number
  ImpuestoDeducible1: number
  TotalDeducible1: number
  CodIdentificadorOrigen: any
  NumeroCorrelativoInicial: string
  DesDimension01: any
  DesDimension02: any
  DesDimension03: any
  DesDimension04: any
  DesDimension05: any
  DesDimension06: any
  DesDimension07: any
  DesDimension08: any
  DesDimension09: any
  DesDimension10: any
  Prioridad: any
  ModoRegistro: any
  ValidacionUsuario: any
  ValidacionFecha: any
  Validado: any
  ValidacionObservacion: any
  Ticket: number
  PrioridadPago: boolean
  TotalNetoNC: any
  TotalImpuestoNC: any
  TotalNC: any
  CodProyectoRelacionado: any
  NroGrupoAprobacion: any
  NumeroAprobadores: any
  UsuarioA: any
  FechaEnvioAprobacion: any
  NroPC: any
  CodFormato: string
  CodTipoDocumentoSunat: string
  CodFormatoSunat: string
}

export interface DocumentResources {
  NroDD: string
  NroD: string
  Correlativo: number
  CodInsumo: string
  Descripcion: string
  Simbolo: string
  Cantidad: number
  Precio: number
  PrecioNeto: number
  PrecioIGV: number
  ParcialNeto: number
  ParcialIGV: number
  Descuento: number
  ValorDescuento: number
  DescuentoTotal: number
  ValorDescuentoTotal: number
  AfectoIGV: boolean
  PorcentajeIGV: number
  Estado: number
  NroPCG: any
  CodPCG: any
  PCG: any
  NroReglaIG: any
  CodReglaIG: any
  ReglaIG: any
  NroIG: any
  CodIG: any
  IG: any
  Neto1: number
  Impuesto1: number
  Total1: number
  Neto2: number
  Impuesto2: number
  Total2: number
  IGAutomatico: boolean
  ValorImpuesto: number
  Detraccion: boolean
  PorcentajeDetraccion: number
  MontoDetraccion: number
  MontoDetraccion1: number
  MontoDetraccion2: number
  NroDEG: any
  CodDEG: any
  DEG: any
  CuentaDebe: any
  CuentaHaber: any
  Percepcion: boolean
  PorcentajePercepcion: number
  MontoPercepcion: number
  MontoPercepcion1: number
  MontoPercepcion2: number
  DestinoVenta: any
  CampoIngresado: string
  DescripcionAlterna: string
  Deducible: boolean
  Correctivo: boolean
  NroD_Anticipo: any
  NumeroDocumentoAnticipo: any
  OrigenCorrectivo: number
  Gratuito: boolean
  CreacionFecha: string
  CreacionUsuario: string
  ActualizacionFecha: any
  ActualizacionUsuario: any
}



const createInitialScenarioConfig = (): ScenarioConfig => ({
    activeGroup: [],
    userGroups: [],
    groupDetailsFields: [],
    vFlexColumnsConfig: null,
    lastUpdatedGrid: '',
    vFlexColumnsConfigParts: [],
    gridConfig: {
        columns: [
            
        ],
        sorting: [],
        filterValue: null,
        selectedRowKeys: [],
        summaryColumns: [
        ],
        groupableColumns: [
        ],
        fixedColumns: [],
        allowedPageSizes: [
            20,
            40
        ],
        filterPanel: {
            filterEnabled: true
        },
        searchText: "",
        pageIndex: 0,
        pageSize: 20
    },
})

const initialState: AccountEntries = {
    documentResources: [],
    documentData: {} as DocumentData,
    scenarios: {
        [Scenarios.LibroDiarioEscenario]: createInitialScenarioConfig(),
        [Scenarios.DocumentDetail]: createInitialScenarioConfig(),
    },
    companies: [],
    scheme: [],
    periods: []
}

export const accountEntries = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setCompanies: (state, {payload}: PayloadAction<S10Response<Company[]>>) => {
            state.companies = payload.data;
        },
        setScheme: (state, {payload}: PayloadAction<S10Response<Scheme[]>>) => {
            state.scheme = payload.data;
        },
        setPeriods: (state, {payload}: PayloadAction<S10Response<Period[]>>) => {
            state.periods = cleanDuplicates(
                [
                    ...(state.periods || []), 
                    ...payload.data
                ],
                "CodPeriodoContable",
            ).sort((a, b) => b.CodPeriodoContable - a.CodPeriodoContable);
        },
        setVFlexConfig: (state, {payload}: PayloadAction<ScenarioResponse<VFlexColumnConfig[]>>) => {
            const scenarioConfig = state.scenarios[payload.scenario];
            // const stored = localStorage.getItem('gridState');
            // const parsed = stored ? JSON.parse(stored) : {};
            if (scenarioConfig.vFlexColumnsConfigParts.includes(payload.part)) {
                return state
            }
            scenarioConfig.vFlexColumnsConfigParts = [...scenarioConfig.vFlexColumnsConfigParts, payload.part];
            const finalData = cleanDuplicates(
                [
                    ...(scenarioConfig.vFlexColumnsConfig || []), 
                    ...payload.data
                ],
                payload.scenario === Scenarios.LibroDiarioEscenario ? "CampoCodigo" : "Campo",
            )
                .filter(f => !!f.Caption && f.Caption !== 'NOVISIBLE')
                .sort((a, b) => a.Posicion - b.Posicion) as VFlexColumnConfig[];

            const existingGroupIndexes: Record<string, number | undefined> = {};
            scenarioConfig.gridConfig.columns.forEach((col: any) => {
                if (col.groupIndex !== undefined && col.groupIndex !== null) {
                    existingGroupIndexes[col.dataField] = col.groupIndex;
                }
            });

            scenarioConfig.gridConfig.columns = finalData.map(vc => ({
                visibleIndex: vc.Posicion,
                dataField: payload.scenario === Scenarios.LibroDiarioEscenario ? vc.CampoCodigo : vc.Campo,
                name: vc.Caption,
                caption: vc.CaptionOriginal || vc.Caption,
                dataType: mapDataTypeToDevExpressDataType(vc.TipoDato),
                width: Number((vc.Ancho > 200 ? (vc.Ancho / 10) : vc.Ancho).toFixed(0)),
                visible: vc.Visible,
                fixed: false,
                groupIndex: existingGroupIndexes[vc.CampoCodigo],
                allowFiltering: vc.Filtro,
                summary: vc.Sumar,
                allowGrouping: vc.Campo !== 'OID',
                allowSorting: true,
                allowHiding: true,
            }));
            localStorage.setItem('gridState', JSON.stringify(scenarioConfig.gridConfig));
            scenarioConfig.vFlexColumnsConfig = finalData;
        },
        setGridConfig: (state, {payload}: PayloadAction<GridConfig>) => {
            // state.gridConfig = payload;
        },
        setActiveGroup: (state, {payload}: PayloadAction<ScenarioResponse<ActiveGroupField[]>>) => {
            state.scenarios[payload.scenario].activeGroup = payload.data;


            const fields = payload.data.map(field => field.Campo);
            const scenarioConfig = state.scenarios[payload.scenario];

            let columnsCopy = scenarioConfig.gridConfig.columns.map((col: any) => ({...col}));
            columnsCopy.forEach((col: any) => col.groupIndex = undefined);

            fields.forEach((field, index) => {
                const col = columnsCopy.find(c => c.dataField === field) as any;
                if (col) {
                    col.groupIndex = index;
                }
            });
            scenarioConfig.gridConfig.columns = columnsCopy;
            localStorage.setItem('gridState', JSON.stringify(scenarioConfig.gridConfig));




        },
        setUserGroups: (state, {payload}: PayloadAction<ScenarioResponse<UserGroups[]>>) => {
            state.scenarios[payload.scenario].userGroups = payload.data.sort((a, b) => a.Correlativo - b.Correlativo);
        },
        setResetGroupDetails: (state, {payload}: PayloadAction<Scenarios>) => {
            state.scenarios[payload].groupDetailsFields = [];
        },
        setGroupDetails: (state, {payload}: PayloadAction<ScenarioResponse<GroupDetailField[]>>) => {
            state.scenarios[payload.scenario].groupDetailsFields = payload.data.sort((a, b) => a.Orden - b.Orden);
        },
        setGridGrouping: (state, {payload}: PayloadAction<{ scenario: Scenarios, fields: string[] }>) => {
            const fields = payload.fields;
            const scenarioConfig = state.scenarios[payload.scenario];

            let columnsCopy = scenarioConfig.gridConfig.columns.map((col: any) => ({...col}));
            columnsCopy.forEach((col: any) => col.groupIndex = undefined);

            fields.forEach((field, index) => {
                const col = columnsCopy.find(c => c.dataField === field) as any;
                if (col) {
                    col.groupIndex = index;
                }
            });
            scenarioConfig.gridConfig.columns = columnsCopy;
            localStorage.setItem('gridState', JSON.stringify(scenarioConfig.gridConfig));
        },
        setDocumentData: (state, {payload}: PayloadAction<S10Response<DocumentData[]>>) => {
            state.documentData = payload.data[0];
        },
        resetDocumentData: (state) => {
            state.documentData = {} as DocumentData;
            state.documentResources = [];
        },
        setDocumentResources: (state, {payload}: PayloadAction<S10Response<DocumentResources[]>>) => {
            state.documentResources = payload.data;
        }

        
    }
})


export const { 
    setGridGrouping,
    setResetGroupDetails,
    setGroupDetails,
    setCompanies,
    setActiveGroup,
    setUserGroups,
    setScheme,
    setPeriods,
    setVFlexConfig,
    setGridConfig,
    setDocumentData,
    resetDocumentData,
    setDocumentResources,
} = accountEntries.actions
